import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {Observable, throwError} from 'rxjs';
import {environment} from '../../environments/environment';
import {AppConfig} from '../shared/app-config';
import {catchError, map, publishReplay, refCount} from 'rxjs/operators';
import {Program} from '../model/program';
import {factory} from '../shared/app-logging';
import {ErrorDataService} from './error-data.service';
import {Product} from '../model/product';
import {Operator} from '../model/operator';

const logger = factory.getLogger('OperatorProgramsService');

@Injectable({
  providedIn: 'root'
})
export class OperatorProgramsService {

  private programs: Observable<Program[]>;
  private programsLastCallError = false;

  constructor(private httpClient: HttpClient) {
  }

  public findPrograms(): Observable<Program[]> {
    if (this.programs && !this.programsLastCallError) {
      logger.debug('findPrograms: Cache hit.');
    }
    this.programs = (this.programs && !this.programsLastCallError) ? this.programs : this.fetchPrograms();
    return this.programs;
  }

  private fetchPrograms(): Observable<Program[]> {
    return this.httpClient.get(environment.contentLibraryApiBaseUrl + AppConfig.urlPrograms).pipe(map(
      response => {
        const programs = [];

        for (const program of response['data']) {
          if (program.hasOwnProperty('id')) {
            if (program.hasOwnProperty('display_label')) {
              let programName, displayLabel, shortDisplayLabel;

              try {
                programName = JSON.parse(program.program_name).en_US;
              } catch (parseException) {
                logger.warn(`The program name was expected to be a JSON, ` +
                  `taking the raw string as program name:  ${program.program_name}`, parseException);

                programName = program.program_name;
              }

              displayLabel = program.display_label || '';
              shortDisplayLabel = program.short_display_label || program.display_label || '';

              programs.push(Program.newInstance(program.id, program.operator_id, programName, displayLabel, shortDisplayLabel, program.status));
            } else {
              logger.error(`Program display label is missing. Ignoring program item from response ${program.id}`);
            }
          } else {
            logger.error(`Program id is missing. Ignoring program item from response ${program}`);
          }
        }

        logger.debug(`findPrograms: ${programs.length} programs fetched from REST`);

        this.programsLastCallError = false;

        return programs.sort((a: Program, b: Program) => a.displayLabel.localeCompare(b.displayLabel));
      }
      ), catchError((err: Error | HttpErrorResponse) => {
        const message = `In OperatorProgramsService.findPrograms().`;

        this.programsLastCallError = true;

        return throwError(ErrorDataService.overLoadError(err, message));
      }),
      publishReplay(1),
      refCount());
  }

  public getProgramsWithOperator(programs: Program[], operators: Operator[]): Program[] {
    const result: Program[] = [];

    for (const program of programs) {
      const operator = operators.find(op => op.id === program.operator.id);

      if (operator) {
        program.operator = operator;

        result.push(program);
      }
    }

    return result;
  }

  public findProgramsWithOperator(operators: Operator[]): Observable<Program[]> {
    return new Observable<Program[]>(subscriber => {
      let result: Program[];

      this.findPrograms().subscribe(programs => {
        result = this.getProgramsWithOperator(programs, operators);

        subscriber.next(result);
        subscriber.complete();
      });
    });
  }

}
